import { createContext, PropsWithChildren, useContext, useEffect, useMemo } from "react";

import { NotFoundView } from "@/components/_molecules/not-found-view";
import { isWorkspaceSsoEnforcedError } from "@/lib/is-workspace-sso-enforced-error";
import { posthogService } from "@/services/posthog.service";
import { removeWorkspaceSettings, setWorkspaceSettings } from "@/services/workspace.service";

import { WorkspaceSsoRequiredView } from "../components/workspace-sso-required-view";
import { CurrentWorkspace } from "../models/current-workspace-model";

import { useWorkspaceGetByUrl } from "./api/use-workspace-by-url";

type WorkspaceContextType = {
  currentWorkspace: CurrentWorkspace;
};

export interface WorkspaceContextProps extends PropsWithChildren {
  workspaceUrl: string;
  userId: string;
}

export const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

export const CurrentWorkspaceContextProvider = ({ children, workspaceUrl, userId }: WorkspaceContextProps) => {
  const { workspace, loading, error } = useWorkspaceGetByUrl({ workspaceUrl: workspaceUrl });

  const workspaceWithMembership = useMemo(() => {
    if (!workspace) {
      return null;
    }

    const wsm = workspace.workspaceMembershipList.find((wsm) => wsm.userId === userId);

    // `customDomain` and `defaultPublicDashboardId` were added to the
    // workspaceByUrl selection set, but generated types lag the schema until
    // codegen runs against an updated backend. Narrow at the boundary so
    // downstream consumers (settings forms) can read the saved value.
    const publicRouting = workspace as { customDomain?: string | null; defaultPublicDashboardId?: string | null };

    return {
      id: workspace.id,
      name: workspace.name,
      workspaceUrl: workspace.workspaceUrl,
      logo: workspace.logo,
      municipalityList: workspace.municipalityList ?? [],
      customDomain: publicRouting.customDomain ?? null,
      defaultPublicDashboardId: publicRouting.defaultPublicDashboardId ?? null,
      createdAt: workspace.createdAt,
      updatedAt: workspace.updatedAt,
      workspaceMembership: {
        role: wsm?.role ?? "USER",
        status: wsm?.status ?? "SUSPENDED",
      },
      twoFactorAuth: workspace.twoFactorAuth ?? null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace]);

  useEffect(() => {
    if (error) {
      removeWorkspaceSettings();
    }
  }, [error]);

  useEffect(() => {
    if (!workspaceWithMembership) {
      return;
    }

    setWorkspaceSettings(workspaceWithMembership);

    posthogService.register({ workspaceId: workspaceWithMembership.id });
    posthogService.group("workspace", workspaceWithMembership.id, {
      workspaceId: workspaceWithMembership.id,
      workspaceUrl: workspaceWithMembership.workspaceUrl,
      name: workspaceWithMembership.name,
    });
  }, [workspaceWithMembership]);

  if (loading) {
    return <></>;
  }

  if (error) {
    return isWorkspaceSsoEnforcedError(error) ? <WorkspaceSsoRequiredView /> : <NotFoundView />;
  }

  if (!workspaceWithMembership) {
    return <NotFoundView />;
  }

  return (
    <WorkspaceContext.Provider value={{ currentWorkspace: workspaceWithMembership }}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useCurrentWorkspaceContext = (): WorkspaceContextType => {
  const context = useContext(WorkspaceContext);

  if (!context) {
    throw new Error("useCurrentWorkspace must be used within a WorkspaceProvider");
  }

  return context;
};

// This is an unsafe hook that returns the current workspace context or NULL
//
// - Generally it is better to use the **useCurrentWorkspaceContext** hook
// - Only use this hook if cannot be sure that the **useCurrentWorkspaceContext** hook is  available
export const useUnsafeCurrentWorkspaceContext = (): WorkspaceContextType | null => {
  return useContext(WorkspaceContext);
};
